import React, { createContext, useContext, useEffect, useState } from 'react';
import zhCommon from '../i18n/locales/zh/common.json';
import { DEFAULT_LANGUAGE, detectLanguage, languages, normalizeLanguage } from '../i18n/languages';

const localeCache = { [DEFAULT_LANGUAGE]: zhCommon };
const localeLoaders = {
  'zh-Hant': () => import('../i18n/locales/zh-Hant/common.json'),
  en: () => import('../i18n/locales/en/common.json'),
};

function getInitialLanguage() {
  const saved = normalizeLanguage(localStorage.getItem('language'));
  return saved || detectLanguage();
}

function getNestedValue(dictionary, key) {
  return String(key).split('.').reduce((value, part) => {
    if (value && typeof value === 'object' && part in value) return value[part];
    return undefined;
  }, dictionary);
}

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [dictionary, setDictionary] = useState(() => localeCache[language] || zhCommon);

  useEffect(() => {
    const cached = localeCache[language];
    if (cached) {
      setDictionary(cached);
      return;
    }
    const load = localeLoaders[language];
    if (!load) {
      setDictionary(zhCommon);
      return;
    }
    let active = true;
    load().then(module => {
      localeCache[language] = module.default || module;
      if (active) setDictionary(localeCache[language]);
    }).catch(() => {
      if (active) setDictionary(zhCommon);
    });
    return () => { active = false; };
  }, [language]);

  const changeLanguage = code => {
    const nextLanguage = normalizeLanguage(code) || DEFAULT_LANGUAGE;
    localStorage.setItem('language', nextLanguage);
    setLanguage(nextLanguage);
  };

  const t = (key, params = {}) => {
    let text = getNestedValue(dictionary, key);
    if (text === undefined) text = getNestedValue(zhCommon, key);
    if (text === undefined) return key;
    if (typeof text !== 'string') return key;
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
    });
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    return { language: DEFAULT_LANGUAGE, changeLanguage: () => {}, t: key => key };
  }
  return context;
}

export { languages };
export default LanguageContext;
